/**
 * Utility class for Client-Side Encryption (CSE) using the Web Crypto API.
 * Uses AES-GCM for encryption and decryption.
 */
export class CryptoUtils {
    private static ALGORITHM = 'AES-GCM';
    private static IV_LENGTH = 12;

    /**
     * Converts a Base64 key string to a CryptoKey for file operations.
     */
    static async importFileKey(keyBase64: string): Promise<CryptoKey> {
        const keyData = Uint8Array.from(atob(keyBase64), c => c.charCodeAt(0));
        return window.crypto.subtle.importKey(
            'raw',
            keyData,
            this.ALGORITHM,
            false,
            ['encrypt', 'decrypt']
        );
    }

    /**
     * Encrypts a file (ArrayBuffer) using the master key.
     * Returns a concatenated ArrayBuffer: [IV (12 bytes)] + [Encrypted Data]
     */
    static async encryptFile(fileData: ArrayBuffer, masterKeyBase64: string): Promise<ArrayBuffer> {
        const key = await this.importFileKey(masterKeyBase64);
        const iv = window.crypto.getRandomValues(new Uint8Array(this.IV_LENGTH));

        const encrypted = await window.crypto.subtle.encrypt(
            { name: this.ALGORITHM, iv } as any,
            key,
            fileData
        );

        // Concatenate IV and encrypted data
        const result = new Uint8Array(iv.length + encrypted.byteLength);
        result.set(iv, 0);
        result.set(new Uint8Array(encrypted), iv.length);

        return result.buffer;
    }

    /**
     * Decrypts an encrypted file blob.
     * Assumes the first 12 bytes are the IV.
     */
    static async decryptFile(concatenatedBuffer: ArrayBuffer, masterKeyBase64: string): Promise<ArrayBuffer> {
        const key = await this.importFileKey(masterKeyBase64);
        const iv = new Uint8Array(concatenatedBuffer.slice(0, this.IV_LENGTH));
        const encryptedData = concatenatedBuffer.slice(this.IV_LENGTH);

        return window.crypto.subtle.decrypt(
            { name: this.ALGORITHM, iv } as any,
            key,
            encryptedData
        );
    }
}
